import React, {createContext, type ReactNode, useContext} from 'react';

import {getValue} from '../../util/runs';
import type {Key, Run, Value} from '../../util/runTypes';

type BaselineRunContextType = undefined | null | Run;

const RunSelectorBaselineRunContext =
  createContext<BaselineRunContextType>(undefined);

/**
 * Returns the current baseline run from context.
 *
 * @throws If called outside of a RunSelectorBaselineRunProvider.
 * @returns The baseline run, or null if no baseline is set.
 */
export function useBaselineRun(): Run | null {
  const run = useContext(RunSelectorBaselineRunContext);
  if (run === undefined) {
    throw new Error(
      'useBaselineRun must be used within a RunSelectorBaselineRunProvider with a valid run'
    );
  }
  return run;
}

/**
 * Returns a summary metric value from the baseline run.
 * Only supports summary metrics; returns undefined for other key sections.
 *
 * @param key - The metric key to look up. Must have section 'summary'.
 * @returns The metric value, or undefined if no baseline or not a summary key.
 */
export function useBaselineRunSummaryMetric(key: Key): Value | undefined {
  const run = useBaselineRun();
  if (!run || key.section !== 'summary') {
    return undefined;
  }
  return getValue(run, key);
}

type RunSelectorBaselineRunProviderProps = {
  /** The run to use as the baseline for metric comparisons, or null. */
  baselineRun: Run | null;
  children: ReactNode;
};

/**
 * Provides the baseline run to descendant components via context.
 * Used to calculate metric deltas in the run selector table.
 */
export function RunSelectorBaselineRunProvider({
  baselineRun,
  children,
}: RunSelectorBaselineRunProviderProps) {
  return (
    <RunSelectorBaselineRunContext.Provider value={baselineRun}>
      {children}
    </RunSelectorBaselineRunContext.Provider>
  );
}
